import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';
import {
  Button,
  BaseCard,
  BaseCardHeader,
  BaseCardBody,
  Icon,
} from '@unite-us/ui';
import { get, isFunction } from 'lodash';
import { connect } from 'react-redux';
import { BackButton } from 'common/buttons';
import addNotification from 'common/utils/Notifications/actions/AddNotification';
import { withRouter } from 'react-router';
import Notifier from 'common/helpers/Notifier';
import {
  useCreateRecord,
  useFind,
  useFindRecord,
  useInvalidateQueries,
} from 'src/api/APIHooks';
import { SurveyJSFormRenderer, buildResponses } from '@unite-us/ui-surveyjs2';
import {
  hbh1556RideHealthLinkMapping,
  crtb1121HideFieldInForm,
} from 'common/utils/FeatureFlags/flags';
import useRideHealth, {
  RIDE_HEALTH_REFERRAL_FORM,
} from 'src/components/Contacts/components/useRideHealth';
import '../stylesheets/contacts.scss';
import './AssessmentDetailsView.scss';

const contextTypesModel = {
  referral: 'referral',
  case: 'case',
  assistance_request: 'assistance_request',
  person: 'person',
  'Screenings::Screening': null,
};

const SUCCESS_MESSAGE = 'Form Response Saved Successfully';

export function AssessmentDetailsView({
  assessment,
  editMode,
  showEditButton,
  onEditClick,
  onCancel,
  afterSubmit,
  contextType,
  context: contextModelId,
  providerId,
  currentEmployee,
  submissionId,
  hideFieldInForm,
  showLatestFormSubmission,
  rideHealthLinkMapping,
}) {
  const surveyRef = useRef();
  const [isSaving, setIsSaving] = useState(false);

  const formId = assessment.id;

  const viewMode = !!submissionId;

  const assessmentConfiguration = assessment.configuration;

  if (hideFieldInForm) {
    const featureFlagQuestion = assessmentConfiguration.pages[0].elements.find((e) => (
      e.name === 'crtb-1121-hide-field-in-form'
    ));

    if (featureFlagQuestion) {
      const questionIdToHide = featureFlagQuestion.html;
      assessmentConfiguration.pages = assessmentConfiguration.pages.map((page) => ({
        ...page,
        elements: page.elements.map((element) => {
          if (element.id === questionIdToHide) {
            return { ...element, visible: false };
          }
          return element;
        }),
      }));
    }
  }

  // SurveyJs needs title and description at the same level as pages
  let surveyJSON = {
    configuration: {
      title: assessment.name,
      description: assessment.description,
      ...assessmentConfiguration,
    },
  };

  const invalidateQueries = useInvalidateQueries();

  const { createRecord: createFormSubmission } = useCreateRecord('form_submission', {
    api: 'coreApi',
  });

  // Load all the submissions to get the latest (Create new submission flow)
  const {
    data: formSubmissionsData,
    isLoading: isformSubmissionsLoading,
    refetch: refetchFormSubmission,
  } = useFind('form_submission', {
    'context.type': contextType,
    context: contextModelId,
    form: formId,
    'form.form_usages.provider': providerId,
  }, {
    page: {
      number: 1,
      size: 1,
    },
    api: 'coreApi',
    queryConfig: {
      refetchOnWindowFocus: false,
      enabled: !viewMode,
      refetchInterval: false,
      staleTime: Infinity,
      placeholderData: undefined,
    },
  });

  const lastFormSubmission = get(formSubmissionsData, 'data.data', []);
  const formSubmissionById = !!lastFormSubmission[0]?.id && showLatestFormSubmission ?
    lastFormSubmission[0].id : submissionId;

  // load the form submission by id (View Submission Flow)
  const { data: formSubmissionData, isLoading: isLoadingSubmission } = useFindRecord(
    'form_submission',
    formSubmissionById,
    {
      page: {
        number: 1,
        size: 1,
      },
      api: 'coreApi',
      queryConfig: {
        refetchOnWindowFocus: false,
        enabled: viewMode,
        refetchInterval: false,
        staleTime: Infinity,
        placeholderData: undefined,
      },
    },
  );

  const { data: formUsageData, isLoading: isLoadingUsage } = useFind('form_usage', {
    provider: providerId,
    form: formId,
  }, {
    page: {
      number: 1,
      size: 1,
    },
    api: 'coreApi',
    queryConfig: {
      refetchOnWindowFocus: false,
      enabled: !viewMode,
      refetchInterval: false,
      staleTime: Infinity,
      placeholderData: undefined,
    },
  });

  const formUsage = get(formUsageData, 'data.data', [])[0];

  const formSubmission = viewMode ?
    get(formSubmissionData, 'data.data') :
    get(formSubmissionsData, 'data.data[0]');

  const showFormSubmission = viewMode || get(formUsage, 'prepopulate', false);

  const handleSetSurvey = (survey) => {
    surveyRef.current = survey;
  };

  const handleOnCancel = () => {
    if (isFunction(onCancel)) {
      onCancel();
    }
  };

  const handleOnEditClick = () => {
    if (isFunction(onEditClick)) {
      onEditClick();
    }
  };

  const handleOnSubmit = async () => {
    const survey = surveyRef.current;
    if (survey.completeLastPage() && !survey.hasErrors()) {
      // Default save the submission, if want to change it you can pass the onSubmit to override
      const responses = buildResponses(surveyRef.current);
      setIsSaving(true);
      handleOnEditClick();
      let formSubmissionId;
      await createFormSubmission({
        responses,
        form: formId,
        provider: providerId,
        contextType: contextTypesModel[contextType],
        context: contextModelId,
        submitter: currentEmployee.id,
      }, {
        mutationConfig: {
          onSuccess: (result) => {
            invalidateQueries('form_submission');
            Notifier.dispatch('success', SUCCESS_MESSAGE);
            formSubmissionId = result.data.data.id;
          },
        },
      });

      refetchFormSubmission();
      setIsSaving(false);

      if (isFunction(afterSubmit)) afterSubmit(formSubmissionId, formUsage?.usage_type);
    }
  };

  const iconDiv = (
    <div
      id="assessment-edit-btn"
      onClick={handleOnEditClick}
      role="button"
      onKeyDown={handleOnEditClick}
      tabIndex={0}
    >
      <Icon icon="IconPencil" style={{ svg: { width: '1em', height: '1em' } }} />
      <p className="assessment-edit-btn__text">Edit</p>
    </div>
  );

  const subheaderText = get(assessment, 'notes_text');

  const title = (
    <span className="assessment-details-view__header">
      {get(assessment, 'name', '')}
      {
        subheaderText && (
          <small className="assessment-details-view__subheader">
            {`(${subheaderText})`}
          </small>
        )
      }

    </span>
  );

  const isRideHealthReferralForm = get(surveyJSON, 'configuration.title') === RIDE_HEALTH_REFERRAL_FORM;
  const { rideHealthConfig, updatedSurveyJSON } = isRideHealthReferralForm ? useRideHealth({
    employee: currentEmployee,
    contextModelId,
    contextType,
    rideHealthLinkMapping,
    surveyJSON,
  }) : { updatedSurveyJSON: null };
  if (updatedSurveyJSON) {
    surveyJSON = updatedSurveyJSON;
  }

  const isLoading = isformSubmissionsLoading || isLoadingSubmission || isLoadingUsage;

  if (isRideHealthReferralForm && !rideHealthConfig) { return null; }

  return (
    <div className="container-fluid assessment-details-view">
      <div className="row">
        <div className="col-sm-10 offset-sm-1 assessments-show">
          <BackButton />
          <BaseCard>
            <BaseCardHeader
              title={title}
            >
              {(!editMode && showEditButton) && iconDiv}
            </BaseCardHeader>
            <BaseCardBody withPadding>
              {isLoading && 'Loading...'}
              {isSaving && 'Saving...'}
              {(!isLoading && !isSaving) && (
                <SurveyJSFormRenderer
                  formData={surveyJSON}
                  formSubmission={showFormSubmission ? formSubmission : null}
                  editMode={editMode}
                  onSurveyModelSet={handleSetSurvey}
                />
              )}
              <div className="assessment-details-view__actions">
                {editMode && !isLoading && !isSaving && (
                  <>
                    <Button
                      id="cancel-btn"
                      className="form-cancel-button"
                      label="Cancel"
                      onClick={handleOnCancel}
                    />
                    <Button
                      id="save-btn"
                      className="form-submit-button ml-one"
                      label="Save"
                      onClick={handleOnSubmit}
                      primary
                    />
                  </>
                )}
              </div>
            </BaseCardBody>
          </BaseCard>
        </div>
      </div>
    </div>
  );
}

AssessmentDetailsView.propTypes = {
  assessment: PropTypes.object.isRequired,
  editMode: PropTypes.bool.isRequired,
  onCancel: PropTypes.func,
  onEditClick: PropTypes.func,
  showEditButton: PropTypes.bool,
  context: PropTypes.string,
  contextType: PropTypes.string,
  providerId: PropTypes.string.isRequired,
  currentEmployee: PropTypes.object.isRequired,
  afterSubmit: PropTypes.func,
  submissionId: PropTypes.string,
  hideFieldInForm: PropTypes.bool,
  showLatestFormSubmission: PropTypes.bool,
  rideHealthLinkMapping: PropTypes.object.isRequired,
};

AssessmentDetailsView.defaultProps = {
  showEditButton: true,
  onCancel: null,
  onEditClick: null,
  afterSubmit: null,
  submissionId: null,
  context: null,
  contextType: null,
  hideFieldInForm: false,
  showLatestFormSubmission: false,
};

const mapStateToProps = (state) => {
  const rideHealthLinkMapping = hbh1556RideHealthLinkMapping(state);
  return {
    providerId: state.session.groupId,
    currentEmployee: state.globalState.currentEmployee,
    hideFieldInForm: crtb1121HideFieldInForm(state),
    rideHealthLinkMapping,
  };
};

export default withRouter(connect(mapStateToProps, {
  addNotification,
})(AssessmentDetailsView));
